import Phaser from "phaser";
import { installLetterboxFit } from "../ui/responsive";
import { equipSkin, getOrCreatePlayerId, loadPlayerMemory, loadSettings, purchaseSkin, purchaseUpgrade } from "../../ai/localMemory";
import { UPGRADE_IDS, UPGRADES, upgradeCost, type UpgradeId } from "../shop";
import { DEFAULT_SKIN_ID, SKIN_IDS, SKINS, type SkinId } from "../skins";
import { AudioManager } from "../AudioManager";
import { t, type Language } from "../../i18n";
import mapConfig from "../data/mapConfig.json";

/** War Funds armory: spend the meta-currency on permanent, stacking upgrades. */
export class ShopScene extends Phaser.Scene {
  private language: Language = "ko";
  private fundsText?: Phaser.GameObjects.Text;
  private rows: Array<{ id: UpgradeId; level: Phaser.GameObjects.Text; cost: Phaser.GameObjects.Text; buyBg: Phaser.GameObjects.Rectangle; buyLabel: Phaser.GameObjects.Text }> = [];
  private skinSwatches: Array<{ id: SkinId; rect: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }> = [];

  constructor() {
    super("ShopScene");
  }

  create() {
    installLetterboxFit(this);
    this.language = loadSettings().language;

    this.add.image(640, 360, mapConfig.mapAssetKey).setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x08070a, 0.62);
    this.add.rectangle(640, 360, 760, 540, 0x11131b, 0.94).setStrokeStyle(2, 0xc9b878, 0.9);

    this.add.text(320, 120, t(this.language, "shopTitle"), titleStyle("#ffe58e", 28)).setOrigin(0, 0.5);
    this.fundsText = this.add.text(960, 120, "", titleStyle("#a7d8ff", 18)).setOrigin(1, 0.5);

    UPGRADE_IDS.forEach((id, index) => {
      const y = 200 + index * 96;
      const labelKey = `upg_${id}` as const;
      const descKey = `upg_${id}_desc` as const;
      this.add.text(320, y, t(this.language, labelKey as Parameters<typeof t>[1]), titleStyle("#f6f0df", 20));
      this.add.text(320, y + 28, t(this.language, descKey as Parameters<typeof t>[1]), titleStyle("#b9b1a0", 14));
      const level = this.add.text(320, y + 54, "", titleStyle("#9be8ff", 14));
      const cost = this.add.text(700, y + 12, "", titleStyle("#ffe58e", 16)).setOrigin(0, 0.5);

      const buy = this.add.container(880, y + 12);
      const buyBg = this.add.rectangle(0, 0, 110, 40, 0x17364a, 0.96).setStrokeStyle(1, 0x8de7ff, 0.9);
      const buyLabel = this.add.text(0, 0, t(this.language, "buy"), titleStyle("#f7f2df", 16)).setOrigin(0.5);
      buy.add([buyBg, buyLabel]);
      buy.setSize(110, 40).setInteractive({ useHandCursor: true });
      buy.on("pointerover", () => buyBg.setFillStyle(0x235372, 1));
      buy.on("pointerout", () => buyBg.setFillStyle(0x17364a, 0.96));
      buy.on("pointerdown", () => this.tryBuy(id));

      this.rows.push({ id, level, cost, buyBg, buyLabel });
    });

    // Commander Banners — a cosmetic War Funds sink.
    this.add.text(640, 486, t(this.language, "banners"), titleStyle("#ffe58e", 18)).setOrigin(0.5);
    SKIN_IDS.forEach((id, index) => {
      const x = 640 + (index - 2.5) * 96;
      const swatch = this.add.container(x, 530);
      const rect = this.add.rectangle(0, 0, 66, 40, SKINS[id].color, 1).setStrokeStyle(2, 0x1a1410, 0.8);
      const label = this.add.text(0, 30, "", titleStyle("#ffe58e", 13)).setOrigin(0.5);
      swatch.add([rect, label]);
      rect.setInteractive({ useHandCursor: true });
      rect.on("pointerover", () => rect.setScale(1.08));
      rect.on("pointerout", () => rect.setScale(1));
      rect.on("pointerdown", () => this.trySkin(id));
      this.skinSwatches.push({ id, rect, label });
    });

    this.createButton(640, 656, t(this.language, "backToMenu"), () => this.scene.start("MenuScene"));
    this.input.keyboard?.on("keydown-ESC", () => this.scene.start("MenuScene"));
    this.refresh();
  }

  private tryBuy(id: UpgradeId) {
    const result = purchaseUpgrade(getOrCreatePlayerId(), id);
    if (result) {
      AudioManager.playSfx("build");
    } else {
      AudioManager.playSfx("deny");
    }
    this.refresh();
  }

  private trySkin(id: SkinId) {
    const playerId = getOrCreatePlayerId();
    const memory = loadPlayerMemory(playerId);
    const owned = (memory.ownedSkins ?? []).includes(id);
    if (owned) {
      if ((memory.equippedSkin ?? DEFAULT_SKIN_ID) !== id && equipSkin(playerId, id)) AudioManager.playSfx("click");
    } else if (purchaseSkin(playerId, id)) {
      AudioManager.playSfx("build");
    } else {
      AudioManager.playSfx("deny");
    }
    this.refresh();
  }

  private refresh() {
    const memory = loadPlayerMemory(getOrCreatePlayerId());
    this.fundsText?.setText(`${t(this.language, "warFunds")} ${memory.warFunds}`);
    for (const row of this.rows) {
      const lvl = memory.upgrades[row.id] ?? 0;
      const def = UPGRADES[row.id];
      const cost = upgradeCost(row.id, lvl);
      row.level.setText(`${t(this.language, "level")} ${lvl} / ${def.maxLevel}`);
      if (cost === null) {
        row.cost.setText(t(this.language, "maxed"));
        row.buyLabel.setText(t(this.language, "maxed"));
        row.buyBg.setFillStyle(0x2a211b, 0.7).setStrokeStyle(1, 0x8d8678, 0.6);
      } else {
        const affordable = memory.warFunds >= cost;
        row.cost.setText(`${cost} ${t(this.language, "warFunds")}`);
        row.buyLabel.setText(t(this.language, "buy"));
        row.buyBg.setFillStyle(affordable ? 0x17364a : 0x2a211b, affordable ? 0.96 : 0.7).setStrokeStyle(1, affordable ? 0x8de7ff : 0x8d8678, affordable ? 0.9 : 0.6);
      }
    }
    const equippedSkin = memory.equippedSkin ?? DEFAULT_SKIN_ID;
    for (const swatch of this.skinSwatches) {
      const def = SKINS[swatch.id];
      const owned = (memory.ownedSkins ?? []).includes(swatch.id);
      const equipped = equippedSkin === swatch.id;
      swatch.rect.setStrokeStyle(equipped ? 4 : 2, equipped ? 0xffffff : 0x1a1410, equipped ? 1 : 0.8);
      if (equipped) {
        swatch.label.setText(t(this.language, "bannerEquipped")).setColor("#9ff0b0");
      } else if (owned) {
        swatch.label.setText(t(this.language, "bannerEquip")).setColor("#cfe8ff");
      } else {
        swatch.label.setText(`${def.cost}`).setColor(memory.warFunds >= def.cost ? "#ffe58e" : "#9b8f7e");
      }
    }
  }

  private createButton(x: number, y: number, label: string, onClick: () => void) {
    const button = this.add.container(x, y).setDepth(200);
    const frame = this.add.rectangle(0, 0, 150, 40, 0x17364a, 0.96).setStrokeStyle(1, 0xc9b878, 0.9);
    const text = this.add.text(0, 0, label, titleStyle("#f7f2df", 15)).setOrigin(0.5);
    button.add([frame, text]);
    button.setSize(150, 40).setInteractive({ useHandCursor: true });
    button.on("pointerover", () => frame.setFillStyle(0x235372, 1));
    button.on("pointerout", () => frame.setFillStyle(0x17364a, 0.96));
    button.on("pointerdown", onClick);
  }
}

function titleStyle(color: string, fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
    fontSize: `${fontSize}px`,
    color,
    stroke: "#08070a",
    strokeThickness: 3
  };
}
